import React, { useState, useEffect } from 'react';
import LoginForm from './components/LoginForm';
import ForgotPasswordForm from './components/ForgotPasswordForm';
import './App.css';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [message, setMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    // Set fade-in animation on mount
    setFadeIn(true);
    
    // Handle message display and auto-dismiss
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Animation effect for view transitions
  useEffect(() => {
    if (showForgotPassword || isLoggedIn) {
      setFadeIn(false);
      setTimeout(() => setFadeIn(true), 50);
    }
  }, [showForgotPassword, isLoggedIn]);

  const handleLoginSuccess = (msg) => {
    setFadeIn(false);
    setTimeout(() => {
      setIsLoggedIn(true);
      setMessage(msg || 'Login successful');
      setTimeout(() => setFadeIn(true), 50);
    }, 200);
  };

  const handleLogout = () => {
    setFadeIn(false);
    setTimeout(() => {
      setIsLoggedIn(false);
      setMessage('You have been signed out');
      setTimeout(() => setFadeIn(true), 50);
    }, 200);
  };

  const handleForgotPassword = () => {
    setFadeIn(false);
    setTimeout(() => {
      setShowForgotPassword(true);
      setTimeout(() => setFadeIn(true), 50);
    }, 200);
  };

  const handleBackToLogin = () => {
    setFadeIn(false);
    setTimeout(() => {
      setShowForgotPassword(false);
      setTimeout(() => setFadeIn(true), 50);
    }, 200);
  };

  const handleResetRequest = (username) => {
    setMessage('If your account exists, password reset instructions have been sent.');
    setTimeout(() => {
      setShowForgotPassword(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen flex items-stretch">
      {/* Left decorative section */}
      <div className="hidden lg:block lg:w-1/2 xl:w-3/5 relative overflow-hidden bg-gradient">
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-grid"></div>
        
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          <div>
            <div className="flex items-center">
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center">
                <img src="/logo_color.png" alt="Takina Space" className="h-14" />
              </div>
            </div>
          </div>
          
          <div className="max-w-lg">
            <h2 className="text-white text-5xl font-bold mb-6">Developer Portal</h2>
            <p className="text-blue-100 text-xl mb-8">Access all your development resources in one place with Takina's unified platform.</p>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-5 border border-white border-opacity-20">
                <div className="w-12 h-12 rounded-lg bg-[#1192DC] flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
                <h3 className="text-white text-lg font-medium mb-2">Secure Access</h3>
                <p className="text-blue-100 text-sm">Role-based permissions and multi-factor authentication keeping your resources safe.</p>
              </div>
              
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-5 border border-white border-opacity-20">
                <div className="w-12 h-12 rounded-lg bg-[#1192DC] flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"></path>
                  </svg>
                </div>
                <h3 className="text-white text-lg font-medium mb-2">API Console</h3>
                <p className="text-blue-100 text-sm">Interactive documentation and testing tools for all internal and external APIs.</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center text-blue-200 text-sm">
            <div className="w-2 h-2 rounded-full bg-green-400 mr-2"></div>
            System Status: All services operational
          </div>
        </div>
      </div>
      
      {/* Right content section */}
      <div className="w-full lg:w-1/2 xl:w-2/5 bg-white flex items-center justify-center p-6">
        <div className={`w-full max-w-md transform transition-all duration-500 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {!isLoggedIn ? (
            <div>
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center lg:hidden">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-md">
                    <img src="/logo_color.png" alt="Takina Space" className="h-12" />
                  </div>
                </div>
                <div className="text-sm text-gray-500 flex items-center lg:ml-auto">
                  <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                  System online
                </div>
              </div>
              
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {showForgotPassword ? 'Reset Password' : 'Welcome Back'}
                </h2>
                <p className="text-gray-500">
                  {showForgotPassword 
                    ? 'Enter your username to receive reset instructions'
                    : 'Sign in to continue to Developer Portal'
                  }
                </p>
              </div>
              
              {showForgotPassword ? (
                <ForgotPasswordForm 
                  onBackToLogin={handleBackToLogin} 
                  onResetRequest={handleResetRequest}
                />
              ) : (
                <LoginForm 
                  onLoginSuccess={handleLoginSuccess} 
                  onForgotPassword={handleForgotPassword}
                />
              )}
            </div>
          ) : (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-green-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Login Successful</h2>
              <p className="text-gray-500 mb-8">You are now signed in to the Takina Space Developer Portal.</p>
              
              <button 
                onClick={handleLogout}
                className="inline-flex items-center justify-center w-full py-3 px-4 bg-[#1192DC] hover:bg-[#0d80c0] text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1192DC]"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                </svg>
                Sign Out
              </button>
            </div>
          )}
          
          {/* Message notification */}
          {message && (
            <div className={`mt-6 p-4 rounded-lg ${isLoggedIn ? 'bg-green-50 border border-green-100 text-green-800' : 'bg-red-50 border border-red-100 text-red-800'} text-sm animate-slideInUp`}>
              {message}
            </div>
          )}
          
          {/* Copyright footer */}
          <div className="mt-12 text-center">
            <p className="text-xs text-gray-500">
              © {new Date().getFullYear()} Takina Space. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;